'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';

import PropTypes from 'prop-types';

import { favoritesUtils } from '../utils/favorites';

export const STATE = {
    INITIAL: 'INITIAL',

    LOADING: 'LOADING',
    LOADED: 'LOADED',
    ERROR: 'ERROR',
};

export const FavoritesContext = createContext();

export const useFavorites = () => {
    const context = useContext(FavoritesContext);
    if (!context) {
        throw new Error('useFavorites must be used within a FavoritesProvider');
    }
    return context;
};

export const FavoritesProvider = ({ children }) => {
    const [favorites, setFavorites] = useState([]);
    const [totalCounts, setTotalCounts] = useState({});
    const [status, setStatus] = useState(STATE.INITIAL);

    // Load favorites from localStorage on mount
    useEffect(() => {
        setStatus(STATE.LOADING);
        try {
            const storedFavorites = favoritesUtils.getFavoritesFromStorage();
            setFavorites(storedFavorites);
            setStatus(STATE.LOADED);
        } catch (error) {
            console.error('Error loading favorites from storage:', error);
            setStatus(STATE.ERROR);
        }
    }, []);

    // Save favorites to localStorage whenever favorites change
    useEffect(() => {
        favoritesUtils.saveFavoritesToStorage(favorites);
    }, [favorites]);

    // Debounced function to sync with server
    const debouncedServerSync = useCallback(
        favoritesUtils.debounce(async (itemType, itemId, action) => {
            const countKey = `${itemType}:${itemId}`;
            try {
                if (action === 'add') {
                    const result = await favoritesUtils.addFavoriteToServer(
                        itemType,
                        itemId
                    );
                    // Update with server response (corrects optimistic update if needed)
                    setTotalCounts(prev => ({
                        ...prev,
                        [countKey]: result.totalCount,
                    }));
                } else if (action === 'remove') {
                    const result =
                        await favoritesUtils.removeFavoriteFromServer(
                            itemType,
                            itemId
                        );
                    // Update with server response (corrects optimistic update if needed)
                    setTotalCounts(prev => ({
                        ...prev,
                        [countKey]: result.totalCount,
                    }));
                }
            } catch (error) {
                console.error('Error syncing with server:', error);
                // On error, we could revert the optimistic update
                // For now, we'll let the optimistic update stand
                // Could show a toast notification here
            }
        }, 500),
        []
    );

    const addFavorite = useCallback(
        async item => {
            const { id, type } = item;

            if (!id) {
                console.error('No ID provided for favorite item');
                return;
            }

            const countKey = `${type}:${id}`;

            // Check if already favorited to prevent duplicate count increments
            const alreadyFavorited = favoritesUtils.isFavorited(
                favorites,
                id,
                type
            );

            // Optimistically update local state
            setFavorites(prev =>
                favoritesUtils.addFavoriteToList(prev, {
                    id,
                    type,
                })
            );

            // Optimistically increment count only if not already favorited
            if (!alreadyFavorited) {
                setTotalCounts(prev => ({
                    ...prev,
                    [countKey]: (prev[countKey] || 0) + 1,
                }));
            }

            // Sync with server (debounced)
            debouncedServerSync(type, id, 'add');
        },
        [debouncedServerSync, favorites]
    );

    const removeFavorite = useCallback(
        async (id, type) => {
            const countKey = `${type}:${id}`;

            // Check if actually favorited to prevent unnecessary count decrements
            const isFavorited = favoritesUtils.isFavorited(favorites, id, type);

            // Optimistically update local state
            setFavorites(prev =>
                favoritesUtils.removeFavoriteFromList(prev, id, type)
            );

            // Optimistically decrement count only if actually favorited
            if (isFavorited) {
                setTotalCounts(prev => ({
                    ...prev,
                    [countKey]: Math.max((prev[countKey] || 0) - 1, 0),
                }));
            }

            // Sync with server (debounced)
            debouncedServerSync(type, id, 'remove');
        },
        [debouncedServerSync, favorites]
    );

    const isFavorited = useCallback(
        (id, type) => {
            return favoritesUtils.isFavorited(favorites, id, type);
        },
        [favorites]
    );

    const getFavoritesByType = useCallback(
        type => {
            return favoritesUtils.getFavoritesByType(favorites, type);
        },
        [favorites]
    );

    const getFavoriteCounts = useCallback(async items => {
        if (!items || items.length === 0) return {};

        try {
            const counts = await favoritesUtils.getFavoriteCounts(items);
            setTotalCounts(prev => ({ ...prev, ...counts }));
            return counts;
        } catch (error) {
            console.error('Error getting favorite counts:', error);
            return {};
        }
    }, []);

    const clearAllFavorites = useCallback(() => {
        setFavorites([]);
        setTotalCounts({});
    }, []);

    const value = {
        favorites: favoritesUtils.sortFavoritesByDate(favorites),
        totalCounts,
        status,
        initialized: status === STATE.LOADED,
        addFavorite,
        removeFavorite,
        isFavorited,
        getFavoritesByType,
        getFavoriteCounts,
        clearAllFavorites,
    };

    return (
        <FavoritesContext.Provider value={value}>
            {children}
        </FavoritesContext.Provider>
    );
};

FavoritesProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
